import { motion } from 'framer-motion';
import { useState } from 'react';
import { FaCloudUploadAlt, FaSpinner } from "react-icons/fa";
import { IoMdClose } from "react-icons/io";
import { MdDelete } from "react-icons/md";
import { toast } from 'react-toastify';
import SummaryApi from '../common';
import { request } from '../helpers/request';
import uploadImage from '../helpers/uploadImage';

const CategoryForm = ({
  onClose,
  callFunc,
  categoryData
}) => {
  const [data, setData] = useState({
    name: categoryData?.name || "",
    description: categoryData?.description || "",
    productImage: categoryData?.productImage || [], // keeping array for consistency with backend model
    _id: categoryData?._id || ""
  });
  
  const [isUploading, setIsUploading] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleOnChange = (e) => {
    const { name, value } = e.target;
    setData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleUploadImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsUploading(true);
    const uploadImageCloudinary = await uploadImage(file);

    setIsUploading(false);

    if(uploadImageCloudinary?.url){
        setData((prev) => ({
            ...prev,
            productImage: [uploadImageCloudinary.url] // Replace existing image, categories usually have one main image
        }));
    } else {
        toast.error("Image upload failed");
    }
  };

  const handleDeleteImage = () => {
    setData((prev) => ({
      ...prev,
      productImage: []
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
        const endpoint = categoryData?._id
          ? SummaryApi.updateCategory
          : SummaryApi.createCategory;
        
        const response = await request({ 
            url: endpoint.url,
            method: endpoint.method
        }, {
            body: JSON.stringify(data)
        });

        const result = await response.json();

        if (result.success) {
            toast.success(result.message);
            onClose();
            callFunc();
        } else {
            toast.error(result.message);
        }
    } catch (error) {
        toast.error("An error occurred");
        console.error(error);
    } finally {
        setLoading(false);
    }
  };

  return (
    <div className='fixed inset-0 w-full h-full z-50 flex items-center justify-center p-4'>
        <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className='absolute inset-0 bg-primary/40 backdrop-blur-sm'
            onClick={onClose}
        />

        <motion.div 
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            className='relative bg-white w-full max-w-lg rounded-[2.5rem] shadow-premium overflow-hidden flex flex-col max-h-[90vh]'
        >
            <div className='px-8 py-6 border-b border-surface-border flex items-center justify-between'>
                <h1 className='text-2xl font-black text-primary tracking-tight'>
                    {categoryData?._id ? "Edit Category" : "Add Category"}
                </h1>
                <button 
                    onClick={onClose}
                    className='w-10 h-10 flex items-center justify-center rounded-full bg-surface-muted text-gray-400 hover:bg-primary hover:text-white transition-all'
                >
                    <IoMdClose size={20}/>
                </button>
            </div>

            <form onSubmit={handleSubmit} className='p-8 overflow-y-auto custom-scrollbar space-y-6'>
                <div className='space-y-2'>
                    <label className='text-xs font-black uppercase tracking-widest text-gray-400'>Category Name</label>
                    <input 
                        type='text' 
                        name='name'
                        value={data.name}
                        onChange={handleOnChange}
                        placeholder='e.g., Helmets, Accessories'
                        className='w-full bg-surface-muted/30 border-2 border-surface-border rounded-2xl px-5 py-4 font-bold text-primary focus:border-primary transition-all outline-none'
                        required
                    />
                </div>

                <div className='space-y-2'>
                    <label className='text-xs font-black uppercase tracking-widest text-gray-400'>Cover Image</label>
                    <div className='bg-surface-muted/30 border-2 border-dashed border-surface-border rounded-2xl p-4'>
                        {data.productImage[0] ? (
                            <div className='relative h-48 w-full group'>
                                <img 
                                    src={data.productImage[0]} 
                                    alt="Category" 
                                    className='w-full h-full object-contain rounded-xl' 
                                />
                                <button
                                    type="button"
                                    onClick={handleDeleteImage}
                                    className='absolute top-2 right-2 p-2 bg-red-500 text-white rounded-xl opacity-0 group-hover:opacity-100 transition-opacity shadow-lg'
                                >
                                    <MdDelete size={20}/>
                                </button>
                            </div>
                        ) : (
                            <label className='flex flex-col items-center justify-center h-48 cursor-pointer hover:bg-surface-muted/50 transition-colors rounded-xl'>
                                {isUploading ? (
                                    <div className='flex flex-col items-center gap-2'>
                                        <FaSpinner className='animate-spin text-accent' size={24}/>
                                        <span className='text-xs font-bold text-gray-400'>Uploading...</span>
                                    </div>
                                ) : (
                                    <div className='flex flex-col items-center gap-2'>
                                        <FaCloudUploadAlt className='text-gray-300' size={40}/>
                                        <span className='text-xs font-bold text-gray-400'>Click to upload image</span>
                                    </div>
                                )}
                                <input type='file' className='hidden' accept="image/*" onChange={handleUploadImage} disabled={isUploading}/>
                            </label>
                        )}
                    </div>
                </div>

                <div className='space-y-2'>
                    <label className='text-xs font-black uppercase tracking-widest text-gray-400'>Description</label>
                    <textarea 
                        name='description'
                        value={data.description}
                        onChange={handleOnChange}
                        rows={4}
                        placeholder='Brief description of the category...'
                        className='w-full bg-surface-muted/30 border-2 border-surface-border rounded-2xl px-5 py-4 font-medium text-primary focus:border-primary transition-all outline-none resize-none'
                    />
                </div>

                <button 
                    className='w-full py-5 bg-primary text-white font-black rounded-2xl hover:bg-accent transition-all shadow-xl shadow-primary/20 active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed' 
                    disabled={loading || isUploading}
                >
                    {loading ? "Saving..." : (categoryData?._id ? "Update Category" : "Create Category")}
                </button>
            </form>
        </motion.div>
    </div>
  );
};

export default CategoryForm;
